import React, { useEffect, useState } from 'react';
import { CalendarDays, ChevronDown, FolderKanban, MessageSquare, Minus, Plus, Sparkles, Users } from 'lucide-react';

// Mes projets actifs (un rôle et des heures ces 4 dernières semaines), du plus travaillé au moins travaillé : rôles,
// l'avancement en une ou deux phrases et les nouveautés de la semaine (tenus par Muse dans son carnet, chaque soir),
// les prochains jalons. Les autres projets où l'on a un rôle sont juste listés.


export const formatHours = (value) => `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;

const shortDate = (iso) => new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' }).format(new Date(`${iso.slice(0, 10)}T12:00:00`));

const daysUntil = (iso, today) => Math.round((new Date(`${iso.slice(0, 10)}T12:00:00`) - new Date(`${today}T12:00:00`)) / 86400000);

export const DeadlineChip = ({ deadline, today }) => {
  if (!deadline?.date) return null;
  const days = daysUntil(deadline.date, today);
  const tone = deadline.late || days < 0 ? 'bg-rose-50 text-rose-700' : days <= 7 ? 'bg-amber-50 text-amber-700'
    : 'bg-slate-100 text-slate-600';
  const when = days === 0 ? "aujourd'hui" : days === 1 ? 'demain' : days > 1 && days < 7 ? `dans ${days} j` : shortDate(deadline.date);
  return (
    <span className={`inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`} title={deadline.label}>
      <span className="truncate">{deadline.label}</span> · {when}
    </span>
  );
};

// Saisie au quart d'heure près ; la valeur s'enregistre un instant après le dernier changement.
export const HoursStepper = ({ value, onChange }) => {
  const [text, setText] = useState(String(value || 0).replace('.', ','));
  useEffect(() => { setText(String(value || 0).replace('.', ',')); }, [value]);
  const set = (next) => onChange(Math.min(24, Math.max(0, Math.round(next * 4) / 4)));
  return (
    <div className="flex items-center rounded-lg border border-slate-200 bg-white">
      <button type="button" onClick={() => set((value || 0) - 0.5)} disabled={!value} title="− 30 min"
        className="rounded-l-lg px-1.5 py-1 text-slate-500 hover:bg-slate-100 disabled:text-slate-200"><Minus size={13} /></button>
      <input value={text} inputMode="decimal" aria-label="Heures"
        onChange={(event) => setText(event.target.value)}
        onBlur={() => { const parsed = parseFloat(text.replace(',', '.')); set(Number.isFinite(parsed) ? parsed : 0); }}
        onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
        onFocus={(event) => event.target.select()}
        className="w-10 border-x border-slate-100 bg-transparent py-1 text-center text-sm font-semibold tabular-nums text-slate-800 outline-none" />
      <button type="button" onClick={() => set((value || 0) + 0.5)} title="+ 30 min"
        className="rounded-r-lg px-1.5 py-1 text-slate-500 hover:bg-slate-100"><Plus size={13} /></button>
    </div>
  );
};

const Milestone = ({ item, today }) => {
  const days = daysUntil(item.date, today);
  const tone = item.late ? 'text-rose-600' : days <= 7 ? 'text-amber-600' : 'text-slate-400';
  const when = item.late ? 'en retard' : days === 0 ? "aujourd'hui" : days === 1 ? 'demain' : shortDate(item.date);
  const meeting = item.kind === 'meeting';
  return (
    <li className="flex items-baseline gap-2 text-[13px] leading-snug">
      <span className={`w-16 shrink-0 text-[11px] font-medium tabular-nums ${tone}`}>{when}</span>
      <span className="min-w-0 flex-1 truncate text-slate-700" title={meeting ? `Réunion : ${item.label}` : item.label}>
        {meeting && <CalendarDays size={11} className="mr-1 inline -translate-y-px text-indigo-400" />}
        {item.label}
        {meeting && item.time && <span className="text-slate-400"> · {item.time}</span>}
      </span>
    </li>
  );
};

const News = ({ items, today }) => (
  <div>
    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Quoi de neuf</p>
    <ul className="space-y-1">
      {items.slice(0, 3).map((item) => {
        const days = daysUntil(item.date, today);
        const when = days === 0 ? "aujourd'hui" : days === -1 ? 'hier' : shortDate(item.date);
        return (
          <li key={`${item.date}${item.text}`} className="flex items-baseline gap-2 text-[13px] leading-snug">
            <span className="w-16 shrink-0 text-[11px] tabular-nums text-slate-400">{when}</span>
            <span className="min-w-0 flex-1 text-slate-700 line-clamp-2" title={item.text}>{item.text}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

const ProjectCard = ({ project, today, openProject }) => (
  <article className="flex flex-col rounded-2xl border border-slate-200/80 bg-white shadow-sm transition hover:border-slate-300 hover:shadow-md">
    <div className="flex-1 space-y-2.5 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <button type="button" onClick={() => openProject(project.id)}
            className="block max-w-full truncate text-left text-[15px] font-semibold text-slate-900 hover:text-indigo-700">
            {project.name}
          </button>
          {project.client_name && <p className="truncate text-xs text-slate-500">{project.client_name}</p>}
          {project.roles?.length > 0 && (
            <p className="truncate text-[11px] font-medium text-indigo-600" title={project.roles.join(', ')}>{project.roles.join(' · ')}</p>
          )}
        </div>
        {project.open_points > 0 && (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700" title="Points à aborder avec le client">
            <MessageSquare size={11} /> {project.open_points}
          </span>
        )}
      </div>
      {(project.progress || project.note) && (
        <p className="flex gap-1.5 text-[13px] leading-snug text-slate-600" title="Selon Nexus">
          <Sparkles size={12} className="mt-0.5 shrink-0 text-indigo-400" />
          <span className="line-clamp-3">{project.progress || project.note}</span>
        </p>
      )}
      {project.news?.length > 0 && <News items={project.news} today={today} />}
      {project.milestones?.length > 0 ? (
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">À venir</p>
          <ul className="space-y-1">
            {project.milestones.map((item) => <Milestone key={`${item.kind}${item.label}${item.date}`} item={item} today={today} />)}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Rien à venir.</p>
      )}
    </div>
    {project.team_today?.length > 0 && (
      <div className="rounded-b-2xl border-t border-slate-100 bg-slate-50/60 px-4 py-2">
        <p className="flex items-center gap-1 truncate text-[11px] text-slate-500"><Users size={11} /> {project.team_today.join(', ')} aujourd'hui</p>
      </div>
    )}
  </article>
);

const ProjectCards = ({ data, error, today, openProject, navigate }) => {
  const [showOthers, setShowOthers] = useState(false);
  const cards = data?.cards;
  const others = data?.others || [];
  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <FolderKanban size={16} className="text-slate-400" /> Mes projets actifs
        </h2>
        <button type="button" onClick={() => navigate('/projects')} className="text-xs font-semibold text-slate-500 hover:text-slate-700">Tous les projets</button>
      </div>
      {error ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : !cards ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-slate-100" />)}
        </div>
      ) : (
        <>
          {cards.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400">
              Aucun projet où tu as un rôle et des heures ces 4 dernières semaines.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
              {cards.map((project) => (
                <ProjectCard key={project.id} project={project} today={today} openProject={openProject} />
              ))}
            </div>
          )}
          {others.length > 0 && (
            <div className="mt-3 text-xs text-slate-500">
              <button type="button" onClick={() => setShowOthers((v) => !v)} className="flex items-center gap-1 font-medium hover:text-slate-700">
                <ChevronDown size={13} className={showOthers ? 'rotate-180' : ''} /> {others.length} autre{others.length > 1 ? 's' : ''} projet{others.length > 1 ? 's' : ''} où tu as un rôle, sans heures récentes
              </button>
              {showOthers && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {others.map((project) => (
                    <button key={project.id} type="button" onClick={() => openProject(project.id)} title={project.roles.join(', ')}
                      className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600 hover:border-slate-300 hover:text-slate-800">
                      {project.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ProjectCards;
